import { useRef } from 'react';
import { useSnapshot } from 'valtio';

import Image from '@cartwhl/ui/atoms/Image';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import SubPanelButton from '@cartwhl/ui/molecules/SubPanelButton';

import { characterPickerStore, projectStore, uiStore } from 'stores';

import {
  AVATAR_PICKER_SUBPANEL_KEY,
  CUSTOM_TAB,
  DEFAULT_TAB,
} from '../constants';
import CharactersSubpanel from './CharactersSubpanel';
import { UploadIndicator } from './UploadIndicator';

export default function AvatarPicker() {
  const { subPanelKey } = useSnapshot(uiStore.state);
  const { currentProject } = useSnapshot(projectStore.state);
  const { characters } = useSnapshot(characterPickerStore.state);

  const activeCharacterID = Object.values(
    currentProject?.data?.characters ?? {},
  )[0]?.characterID;
  const activeCharacter = characters?.find(
    (c) => c.jobID === activeCharacterID,
  );

  const ref = useRef<HTMLButtonElement | null>(null);

  const isOpen = subPanelKey === AVATAR_PICKER_SUBPANEL_KEY;
  const defaultCharacterTab =
    activeCharacter?.createdBy === 'FREE_CHARACTERS' ? DEFAULT_TAB : CUSTOM_TAB;

  return (
    <>
      <SubPanelButton
        isOpen={isOpen}
        onClick={() => {
          uiStore.setSubPanelKey(isOpen ? '' : AVATAR_PICKER_SUBPANEL_KEY);
          uiStore.setSubPanelSectionKey(isOpen ? '' : defaultCharacterTab);
        }}
        ref={ref}
      >
        <div className="flex gap-4 lg:flex-col lg:gap-2">
          <div className="-my-2 -ml-2 h-[60px] w-[60px] flex-none lg:-mx-2 lg:-mt-2 lg:mb-0 lg:h-auto lg:w-[calc(100%+16px)]">
            <Image
              className="h-full w-full rounded-lg object-cover lg:aspect-314/138"
              src={activeCharacter?.heroThumbnailPath}
              alt={activeCharacter?.characterName}
            />
          </div>
          <div className="-mb-1.5 flex min-w-0 flex-1 items-center justify-between gap-4">
            <div className="max-w-full text-left">
              <Text variant="studio/body/medium/sm" as="h2">
                Avatar
              </Text>
              <Text
                variant="studio/body/medium/sm"
                as="div"
                className="text-gray-45 truncate"
              >
                {activeCharacter?.characterName}
              </Text>
            </div>
            <div className="flex items-center gap-1">
              <UploadIndicator />
              <ChevronRightIcon className="text-gray-45" />
            </div>
          </div>
        </div>
      </SubPanelButton>
      {isOpen && (
        <CharactersSubpanel
          id={AVATAR_PICKER_SUBPANEL_KEY}
          elementToTrack={ref.current}
        />
      )}
    </>
  );
}
